import type {
  CategoryComparisonBuckets,
  CategoryComparisonItem,
  CategoryCoverage,
  ComparisonRole,
} from "@/hooks/use-career-path";

/**
 * What carries over between two roles, read at category level.
 *
 * Above the capability buckets because it is the headline: "moving role does
 * not mean starting from nothing" is a statement about categories, not about
 * individual skills or competencies. A developer moving to AI engineer keeps
 * working in the AI category even when every single capability underneath it
 * changes, and the per-capability buckets below cannot say that — there, the
 * same move looks like total replacement. Originally a competency-only
 * "theme" axis (WI-321); extended to skills once categories and themes merged
 * into one many-to-many concept.
 *
 * The counts on a carried-over category are what show the emphasis shifting:
 * three capabilities on one side and one on the other is the same category
 * carrying very different weight.
 */

function Pills({ items, tone }: { items: CategoryComparisonItem[]; tone: string }) {
  if (items.length === 0) return <p className="text-xs text-muted-foreground">None.</p>;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <li
          key={item.id}
          className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${tone}`}
        >
          <span className="font-medium">{item.name}</span>
          <span className="tabular-nums opacity-70">
            {item.fromCount > 0 && item.toCount > 0
              ? `${item.fromCount} → ${item.toCount}`
              : `${Math.max(item.fromCount, item.toCount)}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function CategoryOverlapSummary({
  from,
  to,
  categories,
  coverage,
}: {
  from: ComparisonRole;
  to: ComparisonRole;
  categories: CategoryComparisonBuckets;
  coverage: CategoryCoverage;
}) {
  const anyCategories =
    categories.overlap.length > 0 || categories.added.length > 0 || categories.removed.length > 0;

  /*
    Under-categorised is NOT the same as "shares nothing", and saying so is
    the whole reason coverage is on the payload. With the catalogue barely
    filed this is the common case, so it has to be the branch that reads best.
  */
  const fromIncomplete = coverage.fromCategorized < coverage.fromCapabilities;
  const toIncomplete = coverage.toCategorized < coverage.toCapabilities;

  if (!anyCategories) {
    const neitherFiled = coverage.fromCategorized === 0 && coverage.toCategorized === 0;
    return (
      <section className="rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        {neitherFiled
          ? `Neither ${from.name} nor ${to.name} has skills or competencies filed under a category yet, so there's nothing to say about what carries over at that level. This isn't the same as having nothing in common.`
          : `No categories are shared or changed between these roles.`}
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-lg border">
      <div className="border-b bg-muted/40 px-4 py-2.5">
        <h3 className="text-xs font-bold uppercase tracking-wide">What carries over</h3>
        <p className="text-xs text-muted-foreground">
          The kinds of capability on each side, rather than the individual ones. A category on both
          sides carries over even when the skills and competencies under it change.
        </p>
      </div>

      <div className="grid gap-4 px-4 py-3 sm:grid-cols-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-info-foreground">
            Carried over · {categories.overlap.length}
          </div>
          <div className="mt-1.5">
            <Pills
              items={categories.overlap}
              tone="border-info/40 bg-info-bg text-info-foreground"
            />
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-success-foreground">
            New in {to.name} · {categories.added.length}
          </div>
          <div className="mt-1.5">
            <Pills
              items={categories.added}
              tone="border-success/40 bg-success-bg text-success-foreground"
            />
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-warning-foreground">
            Not required in {to.name} · {categories.removed.length}
          </div>
          <div className="mt-1.5">
            <Pills
              items={categories.removed}
              tone="border-warning/40 bg-warning-bg text-warning-foreground"
            />
          </div>
        </div>
      </div>

      {(fromIncomplete || toIncomplete) && (
        <p className="border-t px-4 py-2 text-xs text-muted-foreground">
          Partial picture — {from.name} has {coverage.fromCategorized} of{" "}
          {coverage.fromCapabilities} capabilities filed under a category, {to.name}{" "}
          {coverage.toCategorized} of {coverage.toCapabilities}. Anything unfiled is missing from
          this summary.
        </p>
      )}
    </section>
  );
}
